import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  addToast,
  Button,
  Divider,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
} from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useOrganizationMembers } from '~/hooks/useMembers';
import { useAddTeamMember } from '~/hooks/useTeamMembers';

import { Select, SelectItem } from '~/components/Field';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { Modal } from '~/components/Modal';

import type { TeamMember, TeamRole } from '~/types';

/**
 * Zod schema for add team member form.
 */
const addMemberSchema = z.object({
  user_id: z.string().min(1, 'Please select a member'),
  role: z.enum(['team_admin', 'team_member'], {
    message: 'Please select a role',
  }),
});

type AddMemberFormData = z.infer<typeof addMemberSchema>;

interface AddTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamName: string;
  orgId: string;
  currentTeamMembers: TeamMember[];
}

export function AddTeamMemberModal(props: AddTeamMemberModalProps): JSX.Element {
  const { currentTeamMembers, isOpen, onClose, orgId, teamId, teamName } = props;
  const { isLoading: orgMembersLoading, members: orgMembers } = useOrganizationMembers(orgId);
  const addTeamMember = useAddTeamMember();

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
  } = useForm<AddMemberFormData>({
    resolver: zodResolver(addMemberSchema),
    mode: 'onBlur',
    defaultValues: {
      user_id: '',
      role: 'team_member',
    },
  });

  const selectedRole = watch('role');

  // Filter out users who are already team members
  const availableMembers = useMemo(() => {
    const currentMemberIds = new Set(currentTeamMembers.map(m => m.user_id));
    return orgMembers
      .filter(m => !currentMemberIds.has(m.user_id) && m.status === 'active')
      .map(m => {
        let label = m.name || m.email || 'Member';

        if (m.name && m.email) {
          label = `${m.name} (${m.email})`;
        }

        return {
          key: m.user_id,
          label,
        };
      });
  }, [orgMembers, currentTeamMembers]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = (data: AddMemberFormData): void => {
    const selectedMember = availableMembers.find(m => m.key === data.user_id);
    const memberName = selectedMember?.label;

    addTeamMember.mutate(
      {
        teamId,
        data: {
          user_id: data.user_id,
          role: data.role as TeamRole,
        },
      },
      {
        onSuccess: () => {
          addToast({
            description: `${memberName} added to ${teamName}`,
            color: 'success',
          });
          onClose();
        },
        onError: (error: Error) => {
          // Handle specific error cases
          if (error.message.includes('already') || error.message.includes('400')) {
            setError('user_id', {
              type: 'manual',
              message: 'This user is already a member of the team.',
            });
          } else if (error.message.includes('not a member') || error.message.includes('org')) {
            setError('user_id', {
              type: 'manual',
              message: 'User must be an organization member first.',
            });
          } else if (error.message.includes('403')) {
            addToast({
              description: 'You do not have permission to add team members.',
              color: 'danger',
            });
          } else if (error.message.includes('404')) {
            addToast({
              description: 'User not found.',
              color: 'danger',
            });
          } else {
            addToast({
              description: error.message || 'Failed to add team member.',
              color: 'danger',
            });
          }
        },
      },
    );
  };

  const handleClose = (): void => {
    if (!addTeamMember.isPending) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleClose}
      maxWidth="md"
      preventClosing={addTeamMember.isPending}
    >
      <ModalContent>
        <form noValidate onSubmit={e => void handleSubmit(onSubmit)(e)}>
          <ModalHeader>Add Team Member</ModalHeader>
          <Divider />
          <ModalBody>
            <Controller
              name="user_id"
              control={control}
              render={({ field }) => (
                <>
                  <Select
                    data-testid="add-team-member-select"
                    description={
                      availableMembers.length === 0
                        ? 'All organization members are already in this team'
                        : `${availableMembers.length} organization member(s) available`
                    }
                    errorMessage={errors.user_id?.message}
                    isInvalid={!!errors.user_id}
                    isLoading={orgMembersLoading}
                    isRequired
                    label="Organization Member"
                    placeholder="Select a member to add"
                    selectedKeys={field.value ? [field.value] : []}
                    {...field}
                  >
                    {availableMembers.map(member => (
                      <SelectItem key={member.key}>{member.label}</SelectItem>
                    ))}
                  </Select>
                </>
              )}
            />

            <RadioGroup
              errorMessage={errors.role?.message}
              isInvalid={!!errors.role}
              label="Role"
              onValueChange={value => setValue('role', value as 'team_admin' | 'team_member')}
              orientation="horizontal"
              size="sm"
              value={selectedRole}
            >
              <Radio
                classNames={{ labelWrapper: 'ml-2' }}
                description="Standard team access"
                value="team_member"
              >
                Team Member
              </Radio>
              <Radio
                classNames={{ labelWrapper: 'ml-2' }}
                description="Can manage team members"
                value="team_admin"
              >
                Team Admin
              </Radio>
            </RadioGroup>
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Button variant="bordered" onPress={handleClose} isDisabled={addTeamMember.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              isDisabled={addTeamMember.isPending || !isDirty || availableMembers.length === 0}
              data-testid="add-team-member-submit"
            >
              {addTeamMember.isPending ? <LoadingSpinner /> : 'Add Member'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
